// Remove once fields are moved to the shadow DOM
import './light-shadow.scss';

// Import dependencies
import { html, LitElement } from 'lit';
import { customElement, query, property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';

// Import global utils
import { isEmpty } from '@automator/shared/utils/is-empty';

// Import escapeHTML
import { escapeHTML } from '@wordpress/escape-html';

// Import i18n functions
import { __ } from '@wordpress/i18n';

// Import recipe builder services
import { setRecipeSetting } from '@automator/builder/services';

// eslint-disable-next-line require-jsdoc
@customElement( 'uap-builder-publish-settings-edit-dialog' )
export class AutomatorBuilderPublishSettingEditDialog extends LitElement {
	/**
	 * Fields object containing data for the setting.
	 * @type {Object}
	 */
	@property( { type: Object } ) accessor fields = {};

	/**
	 * Fields object containing the attributes of the fields
	 * @type {Array}
	 */
	@property( { type: Array } ) accessor fieldsDirectory = [];

	/**
	 * Unique identifier for the component.
	 * @type {string}
	 */
	@property( { type: String, attribute: 'setting-id' } ) accessor settingID;

	// Returns the component that has the fields of this setting
	// We'll use it to retrieve the values
	@query( 'uap-builder-item-legacy-form' ) accessor $optionsForm;

	/**
	 * Disable the shadow DOM
	 *
	 * @todo Remove this method once fields become web components. This component should be using the shadow DOM.
	 * @return {Element} The element
	 */
	createRenderRoot() {
		return this;
	}

	// eslint-disable-next-line require-jsdoc
	render() {
		return html`
			<uo-dialog
				heading=${ escapeHTML( __( 'Edit setting', 'uncanny-automator' ) ) }

				force-manual-close
				open

				@uap-dialog-close=${ this.handleDialogClose }
			>
				<uap-builder-item-legacy-form
					.fieldsDirectory=${ this.fieldsDirectory.map( field => ( { ...field, item_type: 'publish-setting' } ) ) }
					.fieldsValues=${ this.fields }

					return-format="standard"

					@uap-builder-legacy-form-change=${ ( event ) => {
						// Update the fields, even if they are not saved yet
						// The cancel button will revert the changes, and the save button will save them
						this.fields = event.detail.unsavedFieldValues;
					} }
				></uap-builder-item-legacy-form>

				<uo-button
					slot="primary-action"

					@click=${ this.handleConfirmEdit }
				>
					${ escapeHTML( __( 'Confirm', 'uncanny-automator' ) ) }
				</uo-button>

				<uo-button
					slot="secondary-action"
					dialog-action="close"
					color="secondary"
				>
					${ escapeHTML( __( 'Cancel', 'uncanny-automator' ) ) }
				</uo-button>
			</uo-dialog>
		`;
	}

	/**
	 * Handles updating the filter data on the server
	 *
	 * @return {Promise|undefined} The promise that either updates the setting
	 */
	async handleConfirmEdit() {
		// Check if the fields are available
		if ( isEmpty( this.$optionsForm ) ) {
			return;
		}

		// Validate the form
		if ( ! this.$optionsForm.areFieldsValid ) {
			return;
		}

		return setRecipeSetting(
			this.settingID,
			this.fieldsForRemote,
		).then( response => {
			// Delete this component
			this.handleDialogClose();

			// Send custom event to the document
			document.dispatchEvent( new CustomEvent( 'uap-builder-components-set-recipe-setting', {
				detail: {
					settingID: this.settingID,
					fields: this.fieldsForRemote
				}
			} ) );
		} );
	}

	/**
	 * Creates the object that'll be saved in the database containing the values of the fields
	 *
	 * @return {Object|undefined} The fields
	 */
	get fieldsForRemote() {
		if ( ! isEmpty( this.$optionsForm ) ) {
			return JSON.stringify( this.$optionsForm.unsavedFieldValues );
		}
	}

	/**
	 * Delete this component when something closes the dialog
	 */
	handleDialogClose() {
		this.remove();
	}
}